import axios from 'axios';
import {v4 as uuid} from 'uuid';
import {ERROR_CODES} from '@contentlab/config/errorCodeMap';
import {getUploadLimits} from '@contentlab/config/uploadLimits';
import {
  ProcessEndedEventParams,
  ProcessStartedEventParams,
} from '@contentlab/hooks/analytics/use-process-event/useProcessEvent';
import {
  contentLabActions,
  ErrorKind,
  FileStatus,
  UploadedVideo,
  UploadingFile,
} from '@contentlab/stores/contentLabStore';
import {AspectRatio} from '@contentlab/types/aspectRatio';
import {FeatureName} from '@contentlab/types/project';
import reactQueryClient from '@/utils/reactQueryClient';
import translate from '@/utils/translate';
import assetUpdaters from '@/api/updaters/content-studio/asset';
import {ASSETS, CONTENT_LAB} from '@/hooks/content-studio/consts';

function getAspectRatio(width: number, height: number): AspectRatio | null {
  if (!width || !height) {
    return null;
  }

  // Calculate the actual aspect ratio as a decimal
  const actualRatio = width / height;

  // Define the available aspect ratios with their decimal values
  const aspectRatioMap = {
    [AspectRatio['16:9']]: 16 / 9, // ≈ 1.778
    [AspectRatio['9:16']]: 9 / 16, // ≈ 0.563
    [AspectRatio['1:1']]: 1 / 1, // = 1.0
    [AspectRatio['4:3']]: 4 / 3, // ≈ 1.333
    [AspectRatio['4:5']]: 4 / 5, // = 0.8
  };

  // Find the closest aspect ratio
  let closestRatio: AspectRatio | null = null;
  let minDifference = Infinity;

  Object.entries(aspectRatioMap).forEach(([ratio, decimalValue]) => {
    const difference = Math.abs(actualRatio - decimalValue);
    if (difference < minDifference) {
      minDifference = difference;
      closestRatio = ratio as AspectRatio;
    }
  });

  return closestRatio;
}

export const getFileMetadata = (
  file: File
): Promise<{
  duration_millis: number;
  width: number;
  height: number;
  aspect_ratio: AspectRatio | null;
}> => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve({
        duration_millis: Math.round(video.duration * 1000),
        width: video.videoWidth,
        height: video.videoHeight,
        aspect_ratio: getAspectRatio(video.videoWidth, video.videoHeight),
      });
    };

    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Failed to load video metadata`));
    };

    video.src = url;
  });
};

function failedUploadingFilePlaceholder({
  file,
  feature,
}: {
  file: File;
  feature: FeatureName | string;
}): UploadingFile {
  return {
    file,
    id: null,
    status: FileStatus.ERROR,
    progress: 0,
    error: 'Failed to process file',
    feature,
    metadata: {
      duration_millis: 0,
      width: 0,
      height: 0,
      aspect_ratio: null,
    },
  };
}

export const shortenFileName = (fileName: string) => {
  return fileName.length > 13 ? `${fileName.slice(0, 13)}...` : fileName;
};

/**
 * Maps backend Asset to UploadedVideo for display
 */
export const mapAssetToUploadedVideo = (asset: {
  id: string;
  name: string;
  thumbnailUrl?: string;
  url?: string;
  metadata?: {
    durationMillis?: number;
    aspectRatio?: any;
    width?: number;
    height?: number;
  };
}): Partial<UploadedVideo> => {
  return {
    id: asset.id,
    isUploading: false,
    thumbnail: asset.thumbnailUrl,
    fileName: asset.name || '',
    type: 'Uploads',
    url: asset.url,
    durationMillis: asset.metadata?.durationMillis || 0,
    aspectRatio: asset.metadata?.aspectRatio,
    width: asset.metadata?.width,
    height: asset.metadata?.height,
  };
};

interface OnVideoUploadOptions {
  files: File[];
  feature: FeatureName | string;
  analyticsInteractionId: string;
  analyticsLocation: string;
  processEventHandlers: {
    started: (params: ProcessStartedEventParams) => void;
    ended: (params: ProcessEndedEventParams) => void;
  };
  onFilesSelected?: (files: File[]) => void;
  onUploadStart?: (uploadingFiles: UploadingFile[]) => void;
  onUploadComplete?: (uploadedVideos: UploadedVideo[]) => void;
  onUploadError?: (
    uniqueAssetId: string,
    file: File,
    errorCodes?: string[]
  ) => void;
}

export const onVideoUpload = async ({
  files,
  feature,
  analyticsInteractionId,
  analyticsLocation,
  processEventHandlers,
  onFilesSelected,
  onUploadStart,
  onUploadComplete,
  onUploadError,
}: OnVideoUploadOptions) => {
  const {started, ended} = processEventHandlers;
  const uploadProcessId = uuid();

  onFilesSelected?.(files);

  const handleFileError = ({
    file,
    name,
    errorToReport,
    error,
    errorCodes,
    metadata,
  }: {
    file: File;
    name: string;
    errorToReport: string;
    error?: string;
    errorCodes?: string[];
    metadata?: {
      duration_millis: number;
      width: number;
      height: number;
      aspect_ratio: AspectRatio | null;
    };
  }) => {
    if (onUploadError) {
      const uniqueAssetId = uuid();
      contentLabActions.addAssetUploadProcessId({
        assetId: uniqueAssetId,
        uploadProcessId,
      });
      onUploadError(uniqueAssetId, file, errorCodes);
    } else {
      contentLabActions.addError({
        title: error || translate('Failed to upload'),
        description: name,
        kind: ErrorKind.FAILURE,
      });
    }
    ended({
      endpoint: '',
      process_id: uploadProcessId,
      process_name: 'upload',
      original_id: name,
      outcome_id: name,
      result: 'failure',
      error: errorToReport,
      process_details: JSON.stringify({
        duration: metadata?.duration_millis || 0,
        width: metadata?.width || 0,
        height: metadata?.height || 0,
        size: file.size,
      }),
    });
  };

  const getSignedUrls = async (filesToUpload: UploadingFile[]) => {
    try {
      const response = await assetUpdaters.getSignedUrls(
        filesToUpload.map((f) => ({
          file: f.file,
          metadata: f.metadata,
        }))
      );
      const updatedFiles = filesToUpload.map((file) => {
        contentLabActions.updateUploadingFile({
          name: file.file.name,
          id: response.urls[file.file.name].asset_id,
          status: FileStatus.IN_PROGRESS,
        });
        contentLabActions.addAssetUploadProcessId({
          assetId: response.urls[file.file.name].asset_id,
          uploadProcessId,
        });
        return {
          ...file,
          signedUrl: response.urls[file.file.name].upload_url,
          id: response.urls[file.file.name].asset_id,
        };
      });

      return updatedFiles;
    } catch (error) {
      filesToUpload.forEach((file) => {
        const errorToReport = 'Failed to get upload URL';
        contentLabActions.updateUploadingFile({
          name: file.file.name,
          status: FileStatus.ERROR,
          error: errorToReport,
          progress: 0,
        });
        handleFileError({
          file: file.file,
          name: file.file.name,
          errorToReport,
          metadata: file.metadata,
        });
      });
      return [];
    }
  };

  const handleUpload = async (newFiles: UploadingFile[]) => {
    const filesWithSignedUrls = await getSignedUrls(newFiles);

    // Add to uploadedVideos (user selection) immediately
    contentLabActions.addUploadedVideos(
      filesWithSignedUrls.map((fileObj) => ({
        id: fileObj.id || '',
        isUploading: true,
        type: 'Uploads',
        fileName: fileObj.file.name,
        durationMillis: fileObj.metadata.duration_millis,
        aspectRatio: getAspectRatio(
          fileObj.metadata.width,
          fileObj.metadata.height
        ) as AspectRatio,
        feature: fileObj.feature,
        width: fileObj.metadata.width,
        height: fileObj.metadata.height,
      }))
    );

    const uploadResults = await Promise.allSettled(
      filesWithSignedUrls.map((fileObj) => {
        return uploadFileToGCS(fileObj);
      })
    );

    // Process upload results with proper async handling
    const processResult = async (
      result: PromiseSettledResult<boolean>,
      index: number
    ) => {
      if (result.status === 'rejected') {
        contentLabActions.updateUploadingFile({
          name: newFiles[index].file.name,
          status: FileStatus.ERROR,
          error: 'Upload failed',
        });
        handleFileError({
          file: newFiles[index].file,
          name: newFiles[index].file.name,
          errorToReport: 'Upload failed',
          metadata: newFiles[index].metadata,
        });
      } else if (result.status === 'fulfilled') {
        contentLabActions.updateUploadingFile({
          name: newFiles[index].file.name,
          status: FileStatus.SUCCESS,
        });
        const outcomeId =
          filesWithSignedUrls.find(
            (fileObj) => fileObj.file.name === newFiles[index].file.name
          )?.id || '';

        contentLabActions.updateUploadedVideo({
          id: outcomeId,
          isUploading: false,
        });

        ended({
          endpoint: '',
          process_id: uploadProcessId,
          process_name: 'upload',
          original_id: newFiles[index].file.name,
          outcome_id: outcomeId,
          result: 'success',
          process_details: JSON.stringify({
            duration: newFiles[index].metadata.duration_millis,
            width: newFiles[index].metadata.width,
            height: newFiles[index].metadata.height,
            size: newFiles[index].file.size,
          }),
        });
      }
    };

    await Promise.all(uploadResults.map(processResult));
    onUploadComplete?.(
      filesWithSignedUrls
        .map((file, index) =>
          contentLabActions.getUploadedVideoByAssetId(file.id || '')
        )
        .filter(Boolean) as UploadedVideo[]
    );
    reactQueryClient.invalidateQueries({
      queryKey: [CONTENT_LAB, ASSETS],
    });
  };

  const uploadFileToGCS = async (fileObj: {signedUrl: string; file: File}) => {
    try {
      await axios.put(fileObj.signedUrl, fileObj.file, {
        headers: {
          'Content-Type': fileObj.file.type,
        },
        onUploadProgress: (progress) => {
          contentLabActions.updateUploadingFile({
            name: fileObj.file.name,
            status: FileStatus.IN_PROGRESS,
            progress: progress.progress
              ? Math.round(progress.progress * 100)
              : 0,
          });
        },
      });
      return true;
    } catch (error) {
      const errorToReport = 'Failed to upload file to GCS';
      contentLabActions.updateUploadingFile({
        name: fileObj.file.name,
        status: FileStatus.ERROR,
        error: errorToReport,
      });
      handleFileError({
        file: fileObj.file,
        name: fileObj.file.name,
        errorToReport,
      });
      throw error;
    }
  };

  // Start analytics tracking
  files.forEach((file) => {
    started({
      endpoint: '',
      process_name: 'upload',
      original_id: file.name,
      asset_id: file.name,
      process_id: uploadProcessId,
      interaction_id: analyticsInteractionId,
      source: analyticsLocation,
    });
  });

  // Extract metadata for all files
  const metadataResults = await Promise.allSettled(
    files.map((file) => getFileMetadata(file))
  );

  const newFiles: UploadingFile[] = files.map((file, index) => {
    const metadataResult = metadataResults[index];

    if (metadataResult.status === 'rejected') {
      return failedUploadingFilePlaceholder({file, feature});
    }

    return {
      file,
      id: null,
      status: FileStatus.IN_PROGRESS,
      progress: 0,
      error: undefined,
      metadata: metadataResult.value,
      feature,
    };
  });

  // Validate files
  const uploadLimits = getUploadLimits(feature);

  newFiles.forEach((file) => {
    const errors: string[] = [];
    const errorCodes: string[] = [];

    if (file.status === FileStatus.ERROR) {
      errors.push(file.error || 'Failed to process file');
      errorCodes.push(ERROR_CODES.FILE_METADATA_EXTRACT_FAILED);
    }

    if (file.file.size > uploadLimits.maxFileSizeBytes) {
      file.status = FileStatus.ERROR;
      file.error = 'File is too large';
      errors.push(translate('File is too large'));
      errorCodes.push(ERROR_CODES.FILE_TOO_LARGE);
    }
    if (file.metadata.duration_millis > uploadLimits.maxDurationMillis) {
      file.status = FileStatus.ERROR;
      file.error = 'Video too long';
      errors.push(translate('Video too long'));
      errorCodes.push(ERROR_CODES.FILE_TOO_LONG);
    }
    if (errors.length > 0) {
      const errorToReport = errors.join(', ');

      handleFileError({
        file: file.file,
        name: file.file.name,
        errorToReport,
        error: errorToReport,
        errorCodes,
        metadata: file.metadata,
      });
    }
  });

  const validFiles = newFiles.filter(
    (file) => file.status !== FileStatus.ERROR
  );

  if (validFiles.length > 0) {
    onUploadStart?.(validFiles);
    contentLabActions.addUploadingFiles(validFiles);
    handleUpload(validFiles);
    reactQueryClient.invalidateQueries({
      queryKey: [CONTENT_LAB, ASSETS],
    });
  }
};
